import React, { memo, useContext, useRef, useState } from "react";
import {
	Button,
	Col,
	DatePicker,
	message,
	Radio,
	Row,
	Skeleton,
	Typography,
	Input,
	Divider,
	Select,
} from "antd";

import { SearchOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

import dayjs from "dayjs";
import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { 
	useCalculateComponentHeightSizeHook, 
	useCalculateWindowSizeForTextLength 
} from "../../../../hooks";
import { bank_reconciliation_manager } from "../../../../services";
import { formatToTwoDecimalPlaces } from "../../utils";
import { EntitiesContext } from "../../../../contexts";
import { VirtualizedList } from "./VirtualizedList";

import PropTypes from "prop-types";
import { ModalTransactionDetails } from "./ModalDetails";

const totalAmountTransactionsFounded = (transactions = []) => {
	const total = transactions.reduce((accumulator, item) => {
		const { position, amount } = item;

		if(position === "in"){
			return accumulator += formatToTwoDecimalPlaces(amount);
		}

		if(position === "out"){
			return accumulator -= formatToTwoDecimalPlaces(amount);
		}
		
		return accumulator;
	}, 0);
	
	return formatToTwoDecimalPlaces(total);
};

export const MiniTableTransactions = memo(({
	token,
	selectedTransaction,
	transactions = [], 	
	lastMonthsDetected,
	bankAccount: { id: bankAccountId, currency = "N/A" } = {},
	searchText = "",
	filterOptions: { payedState = "", onPayedState = () => {} } = {},
	onTransactions = () => {},
	onSelectedTransaction = () => {},
	onChangeSearch = () => {},
}) => {
	const modalTransactionDetailsRef = useRef(null);
	
	const { clients, suppliers } = useContext(EntitiesContext);
	const { textLength } = useCalculateWindowSizeForTextLength();

	const { tableHeight } = useCalculateComponentHeightSizeHook({heightDifference: 310});

	const { firstMonthDetected, lastMonthDetected } = lastMonthsDetected;
	const [dateFilterType, setDateFilterType] = useState("single");
	const [dates, setDates] = useState({ firstMonth: "", lastMonth: "" });
	
	const [loading, setLoading] = useState(false);	
	
	const fetchTransactions = async() => {
		if(dates.firstMonth === ""){
			message.error("Porfavor selecciona una fecha para filtrar las transacciones");
			return;
		}
		
		setLoading(true);
		onSelectedTransaction({});

		try {			
			const data = {
				bank_account_id: bankAccountId,
				single: dates.firstMonth !== "" && dates.lastMonth === "" ? dates.firstMonth : null,
				range: dates.firstMonth !== "" && dates.lastMonth !== "" ? [dates.firstMonth, dates.lastMonth] : null,
			};
			
			const res = await bank_reconciliation_manager(token, "bank_transaction", data);

			if(!res){
				setLoading(false);
				return;
			}

			const { transactions: transactionsToSet = [], message: messageRes = "" } = res;
			onTransactions({ transactionsToSet, messageRes, datesFounded: [dates.firstMonth, dates.lastMonth] });
		} catch (error) {
			// 
		} finally {
			setLoading(false);
		}		
	};

	const showEntityName = (entityId, position) => {
		if(position === "in"){
			const clientsFounded = clients.filter((client) => client.id === entityId);
			const { name = "N/A" } = clientsFounded[0] ?? {};
			return name;
		}
		
		if(position === "out"){
			const suppliersFounded = suppliers.filter((client) => client.id === entityId);			
			const { name = "N/A" } = suppliersFounded[0] ?? {};
			return name;
		}
		
		return null;
	};
		
	const MiniTableTransactionsView = (
		<>
			<Title level={3} style={{ textAlign: "center", marginBottom: "10px", marginTop: "-5px" }}>Transacciones</Title>

			<Row style={{ marginRight: "15px", marginLeft: "15px", marginBottom: "15px"}} justify="center">
				<Col flex="auto">
					<Input
						placeholder="Buscar Transacción"
						value={searchText}
						style={{ marginRight: "10px", width: "100%" }}
						onChange={(e) => onChangeSearch(e.target.value)}
						prefix={<SearchOutlined />}
					/>
				</Col>
				<Col style={{ marginBottom: "0px" }}>
					<Select value={payedState} onChange={onPayedState} style={{ width: "120px", marginLeft: "10px" }} >
						<Select.Option value="">Por Defecto</Select.Option>
						<Select.Option value="no">Sin Pagar</Select.Option>
						<Select.Option value="intermediate">Pendiente</Select.Option>
						<Select.Option value="completed">Completado</Select.Option>
					</Select>
				</Col>
			</Row>
			
			<Row style={{ marginRight: "15px", marginLeft: "15px" }} justify="center" align="middle" gutter={[10, 10]} wrap={true}>
								
				<Col flex="auto">
					{dateFilterType === "single" && (
						<DatePicker
							style={{ width: "100%" }}
							picker="month"
							value={dates.firstMonth !== "" ? dayjs(dates.firstMonth) : null}
							onChange={(date) => {
								if (date === null) {
									setDates({ firstMonth: "", lastMonth: "" });
									return;
								}

								const formattedMonthDate = date.format("YYYY-MM");
								setDates({ firstMonth: formattedMonthDate, lastMonth: "" });
							}}
						/>
					)}

					{dateFilterType === "range" && (
						<RangePicker
							style={{ width: "100%" }}
							picker="month"
							value={[
								dates.firstMonth !== "" ? dayjs(dates.firstMonth) : null,
								dates.lastMonth !== "" ? dayjs(dates.lastMonth) : null,
							]}
							onChange={(_, dateString) => {
								setDates({ firstMonth: dateString[0], lastMonth: dateString[1] });
							}}
						/>
					)}
				</Col>

				<Col flex="auto">
					<Radio.Group
						style={{width:"100%"}}
						onChange={(e) => {
							setDateFilterType(e.target.value);
							setDates({ firstMonth: "", lastMonth: "" });
						}}
						value={dateFilterType}
					>
						<Radio.Button style={{width:"50%"}} value="single">Individual</Radio.Button>
						<Radio.Button style={{width:"50%"}} value="range">Rango</Radio.Button>
					</Radio.Group>
				</Col>

				<Col flex="auto">
					<Button type="primary" style={{ width: "100%" }} onClick={fetchTransactions}>
						<SearchOutlined />
					</Button>
				</Col>
			</Row>

			<br />

			<Divider orientation="left">Saldo: {currencyGetter[currency]} {totalAmountTransactionsFounded(transactions)}</Divider>

			<div style={{ display: "flex", flexDirection: "column", height: tableHeight, overflow: "hidden" }}>
				<VirtualizedList
					transactions={transactions}
					selectedTransaction={selectedTransaction}
					onSelectedTransaction={onSelectedTransaction}
					setTransactionToView={modalTransactionDetailsRef.current?.openModal}
					currency={currency}
					currencyGetter={currencyGetter}
					showEntityName={showEntityName}
					textLength={textLength}
					tableHeight={tableHeight}
				/>

				<div style={{
					textAlign: "center",
					padding: "10px",
					borderTop: "1px solid #ddd",
				}}>
					<Text>
						Transacciones Verificadas Disponibles del <strong>{firstMonthDetected}</strong>
						{lastMonthDetected !== "" ? <> al <strong>{lastMonthDetected}</strong></> : ""}
					</Text>
				</div>
			</div>

			<ModalTransactionDetails
				ref={modalTransactionDetailsRef}
				currency={currency}
			/>
		</>
	);
	
	return (
		<>			
			{loading && <Skeleton active/>}
			{!loading && MiniTableTransactionsView}
		</>
	);
}
);

MiniTableTransactions.displayName = "MiniTableTransactions";

MiniTableTransactions.propTypes = {
	token: PropTypes.string.isRequired,
	selectedTransaction: PropTypes.shape({
		id: PropTypes.string,
		transaction_number: PropTypes.string,
	}).isRequired,
	transactions: PropTypes.array,	
	lastMonthsDetected: PropTypes.shape({
		firstMonthDetected: PropTypes.string,
		lastMonthDetected: PropTypes.string,
	}),
	bankAccount: PropTypes.shape({
		id: PropTypes.string.isRequired,
		account_number: PropTypes.string.isRequired,
		currency: PropTypes.string.isRequired,
	}),
	searchText: PropTypes.string,
	filterOptions: PropTypes.object,
	onTransactions: PropTypes.func,
	onSelectedTransaction: PropTypes.func,
	onChangeSearch: PropTypes.func,
};